import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";

const PORTRAIT =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_mrveer-styling/artifacts/l4a7ke9g_IMG_1586_Original.webp";

const LINES = ["Royal Elegance,", "Styled For Your", "Big Day."];

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden bg-[#FDFBF7]"
    >
      <div className="pointer-events-none absolute right-8 top-1/2 hidden -translate-y-1/2 lg:block">
        <span className="vertical-text text-[10px] uppercase tracking-[0.5em] text-[#686152]/60">
          Groom · Family · Personal Styling — Punjab & Worldwide
        </span>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 pb-24 pt-32 lg:grid-cols-12 lg:px-10 lg:pt-40">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            data-testid="hero-overline"
            className="mb-8 text-xs font-semibold uppercase tracking-[0.35em] text-[#C5A059]"
          >
            By Appointment Only · Punjab & Worldwide
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-serif text-4xl font-medium leading-[1.08] tracking-tight text-[#141414] sm:text-5xl lg:text-7xl"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: EASE }}
                >
                  {i === 2 ? (
                    <>
                      <span className="italic text-[#C5A059]">Big Day.</span>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
            data-testid="hero-subtitle"
            className="mt-8 max-w-md text-base font-light leading-relaxed tracking-wide text-[#686152] sm:text-lg"
          >
            I'm Manvir Singh — groom, family & personal stylist. At your home in
            Punjab, or online anywhere in the world, I craft the perfect
            look for your big day.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-book-btn"
              onClick={() =>
                document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })
              }
              className="group flex items-center gap-3 rounded-full bg-[#C5A059] px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-[#0C0C0C] transition-colors duration-300 hover:bg-[#B38C43]"
            >
              Book Appointment
              <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <a
              data-testid="hero-whatsapp-btn"
              href="https://wa.me/918801387013?text=Hello%20Manvir%2C%20I%20want%20to%20book%20a%20styling%20appointment."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-full border border-[#141414]/25 px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#141414] transition-colors duration-300 hover:border-[#C5A059] hover:text-[#C5A059]"
            >
              <MessageCircle size={15} />
              +91 88013 87013
            </a>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, delay: 0.4, ease: EASE }}
            className="relative overflow-hidden"
          >
            <motion.img
              style={{ y: imgY }}
              src={PORTRAIT}
              alt="Manvir Singh — Founder & Head Fashion Stylist at mrveer.in"
              data-testid="hero-portrait"
              className="aspect-[3/4] w-full scale-110 object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C0C]/60 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            data-testid="hero-badge"
            className="absolute -bottom-6 -left-6 border border-[#C5A059]/50 bg-[#FDFBF7]/90 px-6 py-5 backdrop-blur-md"
          >
            <p className="font-serif text-3xl text-[#C5A059]">Manvir Singh</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-[#686152]">
              Founder & Head Stylist
            </p>
          </motion.div>

          <div className="pointer-events-none absolute -right-4 -top-4 h-24 w-24 border-r border-t border-[#C5A059]/50" />
          <div className="pointer-events-none absolute -bottom-4 right-16 h-24 w-24 border-b border-r border-[#C5A059]/30" />
        </div>
      </div>
    </section>
  );
}
